import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import configAxiosInterceptors from 'config/axios/interceptors';
import { loadAppScaffold } from 'store/modules/appScaffold/actions';
import { loadMyUser, mergeMyUser } from 'store/modules/myUser/actions';
import { setNetworkReady } from 'store/modules/network/actions';
import { getRequest } from 'utils/HttpRequest/http-request-utils';

export const USER_ID_URL = 'https://myfarmbot.com/userId';

type UserMeResponse = { id: number };

/**
 * Extracts the internal user ID from the Auth0 user's custom claims.
 * Returns a valid positive integer, or `null` when the claim is absent
 * (e.g. first login via a social provider like Google).
 */
function parseUserIdFromToken(auth0User: Record<string, unknown>): number | null {
  const raw = auth0User[USER_ID_URL];
  if (raw === undefined || raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Resolves the internal user ID by calling `GET /user/me`.
 *
 * The backend's JwtStrategy resolves the user through a multi-step cascade
 * (custom claim -> auth-identity table -> legacy column -> verified email),
 * so this call works even when the JWT lacks the `userId` custom claim.
 */
async function fetchMyUserId(): Promise<number | null> {
  try {
    const { data } = await getRequest<UserMeResponse>('user/me', undefined, false);
    return data.id ?? null;
  } catch {
    return null;
  }
}

interface Props {
  children: JSX.Element;
}

/**
 * A component for Auth0, axios initialisation, and init API calls.
 */
const Auth0TokenHandler = ({ children }: Props) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently, user: auth0User, loginWithRedirect } = useAuth0();
  const isInitialising = useRef(false);

  useEffect(() => {
    configAxiosInterceptors(getAccessTokenSilently, loginWithRedirect);
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (!auth0User) return;
    if (isInitialising.current) return;
    isInitialising.current = true;
    dispatch(setNetworkReady(true));
    const initUser = async (): Promise<void> => {
      // Fast path: the Auth0 post-login Action already resolved the user and
      // embedded the internal ID in the token (username-password / passwordless).
      let userId = parseUserIdFromToken(auth0User);
      // Slow path: the token lacks the userId claim (e.g. first Google login).
      // Ask the backend to resolve the identity from the JWT's sub / email.
      if (!userId) {
        userId = await fetchMyUserId();
      }
      if (!userId) return;
      dispatch(mergeMyUser({ id: userId }));
      dispatch(loadMyUser());
      dispatch(loadAppScaffold());
    };
    initUser();
  }, [auth0User, dispatch]);

  return children;
};

export default Auth0TokenHandler;
